/**
 * @module index
 * @package @geenius/adapters/react
 * @description Implements the React adapter provider and context access
 * helpers. This module owns adapter status initialization for the React
 * runtime package while keeping the shared package framework-agnostic.
 */

"use client";

import {
  ADAPTER_DOMAINS,
  AdapterContextError,
  type AdapterDomain,
  type AdapterStatusInfo,
  type AdminAdapter,
  type AiAdapter,
  type AuthAdapter,
  type DbAdapter,
  DOMAIN_LABELS,
  type FileStorageAdapter,
  getDomainConfig,
  isAdaptersConfigured,
  type LoggerAdapter,
  type PaymentsAdapter,
} from "@geenius/adapters";
import {
  createContext,
  type JSX,
  type ReactNode,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";

type AnyAdapter =
  | DbAdapter
  | AuthAdapter
  | PaymentsAdapter
  | AiAdapter
  | FileStorageAdapter
  | AdminAdapter
  | LoggerAdapter;

/**
 * React adapter bag keyed by domain.
 *
 * @property db Database adapter implementation when configured.
 * @property auth Authentication adapter implementation when configured.
 * @property payments Payments adapter implementation when configured.
 * @property ai AI adapter implementation when configured.
 * @property storage File-storage adapter implementation when configured.
 * @property admin Admin adapter implementation when configured.
 * @property logger Logger adapter implementation when configured.
 */
export interface AdapterSet extends Partial<Record<AdapterDomain, AnyAdapter>> {
  db?: DbAdapter;
  auth?: AuthAdapter;
  payments?: PaymentsAdapter;
  ai?: AiAdapter;
  storage?: FileStorageAdapter;
  admin?: AdminAdapter;
  logger?: LoggerAdapter;
}

/**
 * React adapter context value exposed to hooks and provider consumers.
 *
 * @property adapters Adapter bag passed into the provider.
 * @property statuses Current status record for each adapter domain.
 * @property getAdapter Returns a configured adapter for a specific domain or throws when missing.
 * @property isReady Reports whether a domain is configured and connected.
 * @property isLoading Indicates whether initial health checks are still running.
 */
export interface AdapterContextValue {
  adapters: AdapterSet;
  statuses: Record<AdapterDomain, AdapterStatusInfo>;
  getAdapter: <K extends keyof AdapterSet>(
    domain: K,
  ) => NonNullable<AdapterSet[K]>;
  isReady: (domain: AdapterDomain) => boolean;
  isLoading: boolean;
}

const AdapterContext = createContext<AdapterContextValue | null>(null);

/**
 * Props accepted by the React adapter provider.
 *
 * @property adapters Adapter bag made available to descendant hooks and components.
 * @property children React subtree that should receive adapter context.
 * @property healthCheck Whether the provider should probe configured adapters on mount.
 * @property statusOverrides Optional deterministic status overrides for tests and previews.
 */
export interface AdapterProviderProps {
  adapters: AdapterSet;
  children: ReactNode;
  healthCheck?: boolean;
  statusOverrides?: Partial<Record<AdapterDomain, Partial<AdapterStatusInfo>>>;
}

function getInitialProviderName(
  domain: AdapterDomain,
  adapters: AdapterSet,
  statusOverrides?: Partial<Record<AdapterDomain, Partial<AdapterStatusInfo>>>,
): string {
  const overrideProvider = statusOverrides?.[domain]?.provider;
  if (overrideProvider !== undefined) {
    return overrideProvider;
  }

  if (!adapters[domain]) {
    return "none";
  }

  if (!isAdaptersConfigured()) {
    return "configured";
  }

  return getDomainConfig(domain)?.provider ?? "configured";
}

function createInitialStatuses(
  adapters: AdapterSet,
  healthCheck: boolean,
  statusOverrides?: Partial<Record<AdapterDomain, Partial<AdapterStatusInfo>>>,
): Record<AdapterDomain, AdapterStatusInfo> {
  const initial: Record<AdapterDomain, AdapterStatusInfo> = {
    db: {
      domain: "db",
      provider: "none",
      status: "disconnected",
      lastCheckedAt: Date.now(),
    },
    auth: {
      domain: "auth",
      provider: "none",
      status: "disconnected",
      lastCheckedAt: Date.now(),
    },
    payments: {
      domain: "payments",
      provider: "none",
      status: "disconnected",
      lastCheckedAt: Date.now(),
    },
    ai: {
      domain: "ai",
      provider: "none",
      status: "disconnected",
      lastCheckedAt: Date.now(),
    },
    storage: {
      domain: "storage",
      provider: "none",
      status: "disconnected",
      lastCheckedAt: Date.now(),
    },
    admin: {
      domain: "admin",
      provider: "none",
      status: "disconnected",
      lastCheckedAt: Date.now(),
    },
    logger: {
      domain: "logger",
      provider: "none",
      status: "disconnected",
      lastCheckedAt: Date.now(),
    },
  };

  for (const domain of ADAPTER_DOMAINS) {
    const adapter = adapters[domain];
    initial[domain] = {
      domain,
      provider: getInitialProviderName(domain, adapters, statusOverrides),
      status: adapter
        ? healthCheck
          ? "initializing"
          : "connected"
        : "disconnected",
      lastCheckedAt: Date.now(),
      ...statusOverrides?.[domain],
    };
  }

  return initial;
}

/**
 * Provides the React adapter context and optional health-check lifecycle.
 *
 * @param props Provider props including the adapter bag, child tree, and optional health-check settings.
 * @returns A React context provider that exposes adapters and derived status metadata.
 */
export function AdapterProvider({
  adapters,
  children,
  healthCheck = false,
  statusOverrides,
}: AdapterProviderProps): JSX.Element {
  const [isLoading, setIsLoading] = useState(healthCheck);
  const [statuses, setStatuses] = useState<
    Record<AdapterDomain, AdapterStatusInfo>
  >(() => createInitialStatuses(adapters, healthCheck, statusOverrides));

  useEffect(() => {
    if (!healthCheck) {
      return;
    }

    setIsLoading(true);

    const checks = ADAPTER_DOMAINS.map(async (domain) => {
      const adapter = adapters[domain];
      if (!adapter) {
        return;
      }

      try {
        setStatuses((previous) => ({
          ...previous,
          [domain]: { ...previous[domain], status: "initializing" },
        }));

        if (domain === "auth") {
          await adapters.auth?.getSession();
        }

        setStatuses((previous) => ({
          ...previous,
          [domain]: {
            ...previous[domain],
            status: "connected",
            lastCheckedAt: Date.now(),
          },
        }));
      } catch (error) {
        setStatuses((previous) => ({
          ...previous,
          [domain]: {
            ...previous[domain],
            status: "error",
            error:
              error instanceof Error ? error.message : "Health check failed",
            lastCheckedAt: Date.now(),
          },
        }));
      }
    });

    Promise.allSettled(checks).finally(() => setIsLoading(false));
  }, [adapters, healthCheck]);

  const getAdapter = useCallback(
    <K extends keyof AdapterSet>(domain: K): NonNullable<AdapterSet[K]> => {
      const adapter = adapters[domain];
      if (!adapter) {
        throw new AdapterContextError(
          `${DOMAIN_LABELS[domain] ?? domain} adapter not configured in <AdapterProvider>`,
          "ADAPTER_NOT_CONFIGURED",
        );
      }
      return adapter;
    },
    [adapters],
  );

  const isReady = useCallback(
    (domain: AdapterDomain) =>
      !!adapters[domain] && statuses[domain]?.status === "connected",
    [adapters, statuses],
  );

  const value = useMemo<AdapterContextValue>(
    () => ({
      adapters,
      statuses,
      getAdapter,
      isReady,
      isLoading,
    }),
    [adapters, statuses, getAdapter, isReady, isLoading],
  );

  return (
    <AdapterContext.Provider value={value}>{children}</AdapterContext.Provider>
  );
}

/** Alias matching the React reference variant provider export. */
export const AdaptersProvider = AdapterProvider;

/**
 * Reads the React adapter context from the nearest provider.
 *
 * @returns The active adapter context value with adapters, statuses, and readiness helpers.
 * @throws {AdapterContextError} When called outside of an `<AdapterProvider>` tree.
 */
export function useAdapterContext(): AdapterContextValue {
  const context = useContext(AdapterContext);
  if (!context) {
    throw new AdapterContextError(
      "useAdapterContext must be used within <AdapterProvider>",
      "MISSING_CONTEXT",
    );
  }
  return context;
}
